import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Typography, Grid, Box, Tabs, Tab, Chip, Accordion, AccordionSummary, AccordionDetails, IconButton, SvgIcon, AppBar, Toolbar, Button, TextField, Select, MenuItem, FormControl, InputLabel, Menu, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VotingSystem from '../components/VotingSystem';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, addDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from "firebase/auth";



// TODO: Planning: Things we'll start
  // move swot here 

// TODO: Execution: Extra section for marketing only
  // TODO: content creator program: move influencer here

// TODO: Projection / result per action --> KPIs pro action. Internal & external costs
// TODO: each action should have a link to a planning or content creator section 



// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title);

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Function to get all marketing plans
const getMarketingPlans = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'marketingExecution'));
    const plans: any[] = [];
    querySnapshot.forEach((doc) => {
      plans.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return plans;
  } catch (error) {
    console.error('Error getting marketing plans:', error);
    return [];
  }
};

// Function to get all marketing plans from marketingPlan collection
const getAllMarketingPlans = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'marketingPlan'));
    const plans: any[] = [];
    querySnapshot.forEach((doc) => {
      plans.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return plans;
  } catch (error) {
    console.error('Error getting marketing plans:', error);
    return [];
  }
};

// Function to read marketing data from Firebase subcollection
const readMarketingDataFromFirebase = async (planId: string, documentName: string) => {
  try {
    const docRef = doc(db, 'marketingExecution', planId, documentName, 'main');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log(`No such document: ${documentName} in plan ${planId}`);
      return null;
    }
  } catch (error) {
    console.error(`Error reading ${documentName} from Firebase:`, error);
    return null;
  }
};

// Function to save data to Firebase subcollection
const saveDataToFirebase = async (planId: string, documentName: string, data: any) => {
  try {
    console.log(`Attempting to save data to ${documentName} in plan ${planId}:`, data);
    const docRef = doc(db, 'marketingExecution', planId, documentName, 'main');
    await setDoc(docRef, data, { merge: true });
    console.log(`Data saved to ${documentName} in plan ${planId} successfully`);
  } catch (error) {
    console.error(`Error saving to ${documentName} in plan ${planId}:`, error);
  }
};

// Helper function to ensure actions is always an array
const getActionsArray = (actions: any): any[] => {
  if (!actions) return [];
  if (Array.isArray(actions)) return actions;
  if (typeof actions === 'object') return Object.values(actions);
  return [];
};

// Function to create a new marketing plan
const createNewMarketingPlan = async (planName: string) => {
  try {
    // Create the main plan document
    const planRef = await addDoc(collection(db, 'marketingExecution'), {
      name: planName,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    // Create subcollections with main documents
    const subcollections = ['dashboard', 'strategy', 'plan', 'influencers', 'roadmap', 'execution'];
    
    for (const subcollection of subcollections) {
      const mainDocRef = doc(db, 'marketingExecution', planRef.id, subcollection, 'main');
      await setDoc(mainDocRef, {
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }

    return planRef.id;
  } catch (error) {
    console.error('Error creating new marketing plan:', error);
    return null;
  }
};

// Function to read settings data from Firebase
const readSettingsData = async (documentName: string) => {
  try {
    const docRef = doc(db, 'settings', documentName);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log(`No such document: ${documentName} in settings`);
      return null;
    }
  } catch (error) {
    console.error(`Error reading ${documentName} from settings:`, error);
    return null;
  }
};

// Editable Text Component
const EditableText: React.FC<{
  value: string;
  onSave: (value: string) => void;
  variant?: 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  sx?: any;
  placeholder?: string;
  alwaysShowInput?: boolean;
}> = ({ value, onSave, variant = 'body1', sx = {}, placeholder = '', alwaysShowInput = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (alwaysShowInput || isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        variant="outlined"
        placeholder={placeholder}
        fullWidth
        multiline={!alwaysShowInput && (variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6')}
        minRows={!alwaysShowInput && (variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6') ? 1 : undefined}
        maxRows={!alwaysShowInput && (variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6') ? 3 : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: variant === 'h3' ? '1.5rem' : variant === 'h4' ? '1.5rem' : variant === 'h5' ? '1.25rem' : variant === 'h6' ? '1.25rem' : '1rem',
            fontWeight: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? 'bold' : 'normal',
            minHeight: 'auto',
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          '& .MuiInputBase-input': {
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          ...sx,
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => setIsEditing(true)}
      sx={{
        typography: variant,
        cursor: 'pointer',
        display: 'inline-block',
        minWidth: variant === 'h3' ? '80px' : '60px',
        minHeight: '1.2em',
        border: '1px dashed transparent',
        borderRadius: 1,
        padding: '2px 4px',
        transition: (theme) => theme.transitions.create(['background-color', 'border-color']),
        '&:hover': {
          borderColor: 'divider',
          backgroundColor: 'action.hover',
        },
        ...sx,
      }}
    >
      {value || placeholder}
    </Box>
  );
};

// Editable Number Component
const EditableNumber: React.FC<{
  value: number;
  onSave: (value: number) => void;
  formatCurrency?: boolean;
  variant?: 'h3' | 'h6' | 'body1' | 'body2';
  sx?: any;
}> = ({ value, onSave, formatCurrency = false, variant = 'body1', sx = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  useEffect(() => {
    setEditValue(value.toString());
  }, [value]);

  const handleSave = () => {
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue) && newValue !== value) {
      onSave(newValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        type="number"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        size="small"
        sx={{
          width: '120px',
          '& .MuiOutlinedInput-root': {
            fontSize: variant === 'h6' ? '1.25rem' : '1rem',
          },
          ...sx
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => setIsEditing(true)}
      sx={{
        typography: variant,
        cursor: 'pointer',
        minWidth: '50px',
        display: 'inline-block',
        ...sx
      }}
    >
      {formatCurrency ? `$${value.toLocaleString()}` : value.toLocaleString()}
    </Box>
  );
};

// Editable Array Item Component
const EditableArrayItem: React.FC<{
  value: string;
  onSave: (newValue: string) => void;
  onDelete: () => void;
  variant?: 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  sx?: any;
}> = ({ value, onSave, onDelete, variant = 'body1', sx = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [showControls, setShowControls] = useState(false);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyPress}
        autoFocus
        fullWidth
        variant="outlined"
        multiline={variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6'}
        sx={{ 
          '& .MuiOutlinedInput-root': { 
            fontSize: variant === 'h3' ? '3rem' : variant === 'h4' ? '2.125rem' : variant === 'h5' ? '1.5rem' : variant === 'h6' ? '1.25rem' : '1rem',
            fontWeight: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? 'bold' : 'normal',
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          '& .MuiInputBase-input': {
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          ...sx
        }}
      />
    );
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        '&:hover .array-controls': { opacity: 1 },
        ...sx 
      }}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <Typography 
        variant={variant} 
        sx={{ 
          cursor: 'pointer', 
          flex: 1,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 1 },
        }}
        onClick={() => setIsEditing(true)}
      >
        {value}
      </Typography>
      <Box 
        className="array-controls"
        sx={{ 
          display: 'flex', 
          gap: 0.5, 
          opacity: showControls ? 1 : 0, 
          transition: 'opacity 0.2s',
          ml: 1
        }}
      >
        <IconButton 
          size="small" 
          onClick={onDelete}
          sx={{ 
            color: 'error.main', 
            '&:hover': { backgroundColor: 'error.light', color: 'white' },
            width: 24, 
            height: 24 
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>-</Typography>
        </IconButton>
      </Box>
    </Box>
  );
};

// Add Item Button Component
const AddItemButton: React.FC<{
  onAdd: () => void;
  label: string;
}> = ({ onAdd, label }) => {
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={onAdd}
        startIcon={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>+</Typography>}
        sx={{ 
          borderStyle: 'dashed',
          color: 'text.secondary',
          '&:hover': { 
            borderStyle: 'solid',
            color: 'primary.main',
            borderColor: 'primary.main'
          }
        }}
      >
        Add {label}
      </Button>
    </Box>
  );
};

// Custom SVG Icons
const TwitchIcon = (props: any) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.64,16.32H9.96L8.6,18H6.92L9.4,14.32V8.64H11.08V12.96H11.16L13.16,8.64H14.84L12.24,12.96L14.96,18H13.28L11.64,16.32M5.5,3L4,4.5V18.5L7,21.5H10L12.5,24H15.5L18,21.5V17L21.5,13.5V3H5.5M20,13L17.5,15.5V19.5L15,22H13L11,24V22H7.5L5.5,20V5H18.5V13Z" />
  </svg>
);

const TiktokIcon = (props: any) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 12.31 10v6.3a2.33 2.33 0 1 1-4.66 0V10a6.6 6.6 0 0 0 4.65-6.32 7.42 7.42 0 0 0 .16-1.87v-.11s-.51-.5 0 0a4.28 4.28 0 0 1 4.29-4.28h.34s.51.5 0 0a4.28 4.28 0 0 1-4.29 4.28z"/>
  </svg>
);

// EditableChip Component for Tier/Status
const EditableChip: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onSave: (value: string) => void;
  getColor: (value: string) => "default" | "error" | "warning" | "info" | "success" | "primary" | "secondary";
}> = ({ value, options, onSave, getColor }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    onSave(newValue);
    handleClose();
  };

  const currentOption = options.find(opt => opt.value === value);

  return (
    <>
      <Chip
        label={currentOption ? currentOption.label : value}
        onClick={handleClick}
        color={getColor(value)}
        size="small"
        variant="filled"
        sx={{
          cursor: 'pointer',
          textTransform: 'capitalize',
          minWidth: 80,
          maxWidth: 120,
          fontWeight: 500,
          fontSize: '0.95em',
          overflow: 'visible',
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => handleSelect(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Influencer Card Component
const InfluencerCard: React.FC<{
  influencer: any;
  index: number;
  onSave: (field: string, value: any) => void;
  onDelete: () => void;
}> = ({ influencer, index, onSave, onDelete }) => {
  const [showControls, setShowControls] = useState(false);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'High': return 'error';
      case 'Mid': return 'warning';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'in discussion': return 'warning';
      case 'contacted': return 'info';
      case 'open': return 'default';
      default: return 'default';
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 2 
          }}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
        >
          <EditableText 
            value={influencer.name} 
            onSave={(newValue) => onSave('name', newValue)} 
            variant="h6"
          />
          <Box sx={{ display: 'flex', gap: 1, opacity: showControls ? 1 : 0, transition: 'opacity 0.2s' }}>
            <IconButton 
              size="small" 
              onClick={onDelete}
              sx={{ 
                color: 'error.main', 
                '&:hover': { backgroundColor: 'error.light', color: 'white' },
                width: 24, 
                height: 24 
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>-</Typography>
            </IconButton>
          </Box>
        </Box>

        <EditableText 
          value={influencer.focus} 
          onSave={(newValue) => onSave('focus', newValue)} 
          variant="body2"
          sx={{ color: 'text.secondary', mb: 2 }}
        />

        <EditableText 
          value={influencer.followers} 
          onSave={(newValue) => onSave('followers', newValue)} 
          variant="body2"
          sx={{ fontWeight: 'bold', mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <EditableChip
            value={influencer.tier || 'Mid'}
            options={[
              { value: 'High', label: 'High' },
              { value: 'Mid', label: 'Mid' },
              { value: 'Low', label: 'Low' },
            ]}
            onSave={(newValue) => onSave('tier', newValue)}
            getColor={getTierColor}
          />
          <EditableChip
            value={influencer.status || 'open'}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'contacted', label: 'Contacted' },
              { value: 'in discussion', label: 'In Discussion' },
              { value: 'confirmed', label: 'Confirmed' },
            ]}
            onSave={(newValue) => onSave('status', newValue)}
            getColor={getStatusColor}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton 
              size="small" 
              href={influencer.youtube || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: '#FF0000' }}
              disabled={!influencer.youtube}
            >
              <YouTubeIcon />
            </IconButton>
            <EditableText 
              value={influencer.youtube || ''} 
              onSave={(newValue) => onSave('youtube', newValue)} 
              variant="body2"
              placeholder="YouTube URL"
              alwaysShowInput={true}
              sx={{ 
                minWidth: 120,
                '& .MuiInputBase-input': { 
                  fontSize: '0.75rem',
                  padding: '2px 4px'
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton 
              size="small" 
              href={influencer.twitch || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: '#9146FF' }}
              disabled={!influencer.twitch}
            >
              <SvgIcon component={TwitchIcon} />
            </IconButton>
            <EditableText 
              value={influencer.twitch || ''} 
              onSave={(newValue) => onSave('twitch', newValue)} 
              variant="body2"
              placeholder="Twitch URL"
              alwaysShowInput={true}
              sx={{ 
                minWidth: 120,
                '& .MuiInputBase-input': { 
                  fontSize: '0.75rem',
                  padding: '2px 4px'
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton 
              size="small" 
              href={influencer.twitter || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: '#1DA1F2' }}
              disabled={!influencer.twitter}
            >
              <TwitterIcon />
            </IconButton>
            <EditableText 
              value={influencer.twitter || ''} 
              onSave={(newValue) => onSave('twitter', newValue)} 
              variant="body2"
              placeholder="Twitter URL"
              alwaysShowInput={true}
              sx={{ 
                minWidth: 120,
                '& .MuiInputBase-input': { 
                  fontSize: '0.75rem',
                  padding: '2px 4px'
                }
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// TikTok Creator Card Component
const TikTokCreatorCard: React.FC<{
  creator: any;
  index: number;
  onSave: (field: string, value: any) => void;
  onDelete: () => void;
}> = ({ creator, index, onSave, onDelete }) => {
  const [showControls, setShowControls] = useState(false);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'High': return 'error';
      case 'Mid': return 'warning';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'in discussion': return 'warning';
      case 'contacted': return 'info';
      case 'open': return 'default';
      default: return 'default';
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent 
        sx={{ flexGrow: 1, textAlign: 'center', position: 'relative' }}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        <IconButton 
          size="small" 
          onClick={onDelete}
          sx={{ 
            position: 'absolute',
            right: 4,
            top: 4,
            color: 'error.main', 
            '&:hover': { backgroundColor: 'error.light', color: 'white' },
            width: 24, 
            height: 24,
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.2s'
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>-</Typography>
        </IconButton>

        <EditableText 
          value={creator.name} 
          onSave={(newValue) => onSave('name', newValue)} 
          variant="h6"
          sx={{ mb: 2 }}
        />

        <EditableText 
          value={creator.focus} 
          onSave={(newValue) => onSave('focus', newValue)} 
          variant="body2"
          sx={{ color: 'text.secondary', mb: 2 }}
        />

        <EditableText 
          value={creator.followers} 
          onSave={(newValue) => onSave('followers', newValue)} 
          variant="body2"
          sx={{ fontWeight: 'bold', mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <EditableChip
            value={creator.tier || 'Mid'}
            options={[
              { value: 'High', label: 'High' },
              { value: 'Mid', label: 'Mid' },
              { value: 'Low', label: 'Low' },
            ]}
            onSave={(newValue) => onSave('tier', newValue)}
            getColor={getTierColor}
          />
          <EditableChip
            value={creator.status || 'open'}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'contacted', label: 'Contacted' },
              { value: 'in discussion', label: 'In Discussion' },
              { value: 'confirmed', label: 'Confirmed' },
            ]}
            onSave={(newValue) => onSave('status', newValue)}
            getColor={getStatusColor}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
          <IconButton 
            href={creator.tiktok || '#'} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ color: '#000000' }}
            disabled={!creator.tiktok}
          >
            <SvgIcon component={TiktokIcon} />
          </IconButton>
          <EditableText 
            value={creator.tiktok || ''} 
            onSave={(newValue) => onSave('tiktok', newValue)} 
            variant="body2"
            placeholder="TikTok URL"
            alwaysShowInput={true}
            sx={{ 
              minWidth: 120,
              '& .MuiInputBase-input': { 
                fontSize: '0.75rem',
                padding: '2px 4px'
              }
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`phase-tabpanel-${index}`}
      aria-labelledby={`phase-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface MarketingExecutionProps {
  planId: string;
  planName: string;
  onBackToPlans: () => void;
}

const MarketingExecution: React.FC<MarketingExecutionProps> = ({ planId, planName, onBackToPlans }) => {
  console.log('MarketingExecution render - planId:', planId, 'planName:', planName);
  
  const [view, setView] = useState('dashboard');
  const [activePhase, setActivePhase] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hoveredMilestone, setHoveredMilestone] = useState<number | null>(null);
  
  // State for Firebase data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [strategyData, setStrategyData] = useState<any>(null);
  const [planData, setPlanData] = useState<any>(null);
  const [influencersData, setInfluencersData] = useState<any>(null);
  const [roadmapData, setRoadmapData] = useState<any>(null);
  const [executionData, setExecutionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // State for settings data
  const [marketingUpdates, setMarketingUpdates] = useState<string[]>([]);
  const [marketingTasks, setMarketingTasks] = useState<string[]>([]);

  // State for modals
  const [selectedUpdateType, setSelectedUpdateType] = useState<string>('');
  const [editModalOpen, setEditModalOpen] = useState(false);

  // State for plans dropdown
  const [marketingPlans, setMarketingPlans] = useState<any[]>([]);
  const [plansAnchorEl, setPlansAnchorEl] = useState<null | HTMLElement>(null);
  // Add this state to track which week/phase the plan menu is for
  const [currentPlanIndices, setCurrentPlanIndices] = useState<{ phaseIndex: number, weekIndex: number } | null>(null);

  const phaseLabels = useMemo(() => planData?.phases?.map((p: any) => p.title || '') || [], [planData?.phases]);
  const projectionData = useMemo(() => planData?.phases?.map((p: any) => p.projection || 0) || [], [planData?.phases]);
  const resultData = useMemo(() => planData?.phases?.map((p: any) => p.result || 0) || [], [planData?.phases]);

  const lineChartData = useMemo(() => ({
      labels: phaseLabels,
      datasets: [
          {
              label: 'Projection',
              data: projectionData,
              borderColor: 'rgb(53, 162, 235)',
              backgroundColor: 'rgba(53, 162, 235, 0.5)',
              tension: 0.4,
          },
          {
              label: 'Result',
              data: resultData,
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.5)',
              tension: 0.4,
          },
      ],
  }), [phaseLabels, projectionData, resultData]);

  const handleNavClick = (viewName: string) => {
    setView(viewName);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Function to calculate spending totals by type
  const calculateSpendingTotals = (spending: any) => {
    let internal = 0;
    let external = 0;
    let partner = 0;
    let total = 0;

    Object.entries(spending).forEach(([key, value]: [string, any]) => {
      if (key !== 'Total' && key !== 'Internal' && key !== 'External' && key !== 'Partner') {
        const amount = value.amount || 0;
        const type = value.type || 'internal';
        
        total += amount;
        
        switch (type) {
          case 'internal':
            internal += amount;
            break;
          case 'external':
            external += amount;
            break;
          case 'partner':
            partner += amount;
            break;
        }
      }
    });

    return { internal, external, partner, total };
  };

  // Get color for budget category
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Influencer Marketing': '#4f46e5',
      'Paid Ads': '#7c3aed',
      'Community Prizing': '#db2777',
      'Paid User Acquisition': '#7c3aed'
    };
    return colors[category] || '#6b7280';
  };

  // Calculate budget from spending data
  const calculateBudgetFromSpending = useMemo(() => {
    if (!planData?.phases) return { totalBudget: 0, budgetAllocation: [] };

    const spendingByCategory: { [key: string]: number } = {};
    let totalSpent = 0;

    // Go through all phases and weeks to calculate spending
    planData.phases.forEach((phase: any) => {
      phase.weeks?.forEach((week: any) => {
        if (week.spending) {
          Object.entries(week.spending).forEach(([category, amount]) => {
            if (category !== 'Total' && typeof amount === 'number') {
              spendingByCategory[category] = (spendingByCategory[category] || 0) + amount;
              totalSpent += amount;
            }
          });
        }
      });
    });

    // Convert to budget allocation format
    const budgetAllocation = Object.entries(spendingByCategory).map(([category, amount]) => ({
      label: category,
      value: amount,
      color: getCategoryColor(category)
    }));

    return {
      totalBudget: totalSpent,
      budgetAllocation
    };
  }, [planData?.phases]);

  const budgetData = calculateBudgetFromSpending;

  const doughnutChartData = useMemo(() => ({
    labels: budgetData.budgetAllocation.map(item => item.label),
    datasets: [{
      data: budgetData.budgetAllocation.map(item => item.value),
      backgroundColor: budgetData.budgetAllocation.map(item => item.color),
      borderColor: budgetData.budgetAllocation.map(item => item.color),
      borderWidth: 1
    }]
  }), [budgetData.budgetAllocation]);

  // Save functions for different data types
  const saveDashboardGoals = async (field: string, value: string | number) => {
    if (!dashboardData || !planId) return;
    const updatedData = { ...dashboardData };
    if (!updatedData.goals) updatedData.goals = {};
    updatedData.goals[field] = value;
    setDashboardData(updatedData);
    await saveDataToFirebase(planId, 'dashboard', updatedData);
  };

  const saveDashboardKPIs = async (field: string, value: any) => {
    if (!dashboardData || !planId) return;
    const updatedData = { ...dashboardData };
    if (!updatedData.kpis) updatedData.kpis = {};
    updatedData.kpis[field] = value;
    setDashboardData(updatedData);
    await saveDataToFirebase(planId, 'dashboard', updatedData);
  };

  const saveStrategyData = async (section: string, index: number, field: string, value: any) => {
    if (!strategyData || !planId) return;
    const updatedData = { ...strategyData };
    if (!updatedData[section]) updatedData[section] = [];
    if (!updatedData[section][index]) updatedData[section][index] = {};
    updatedData[section][index][field] = value;
    setStrategyData(updatedData);
    await saveDataToFirebase(planId, 'strategy', updatedData);
  };

  const savePlanData = async (phaseIndex: number, weekIndex: number, field: string, value: any) => {
    if (!planData || !planId) return;
    const updatedData = { ...planData };
    if (!updatedData.phases[phaseIndex].weeks[weekIndex]) {
      updatedData.phases[phaseIndex].weeks[weekIndex] = {};
    }
    updatedData.phases[phaseIndex].weeks[weekIndex][field] = value;
    setPlanData(updatedData);
    await saveDataToFirebase(planId, 'plan', updatedData);
  };

  const saveInfluencersData = (field: string, indexOrValue: number | string, subField?: string, value?: any) => {
    if (!planId) return;
    
    if (typeof indexOrValue === 'number' && subField && value !== undefined) {
      // Updating array item field
      const updatedData = { ...influencersData };
      if (field === 'influencers') {
        updatedData.influencers = [...(updatedData.influencers || [])];
        updatedData.influencers[indexOrValue] = { ...updatedData.influencers[indexOrValue], [subField]: value };
      } else if (field === 'tiktokCreators') {
        updatedData.tiktokCreators = [...(updatedData.tiktokCreators || [])];
        updatedData.tiktokCreators[indexOrValue] = { ...updatedData.tiktokCreators[indexOrValue], [subField]: value };
      }
      setInfluencersData(updatedData);
      saveDataToFirebase(planId, 'influencers', updatedData);
    } else if (typeof indexOrValue === 'string') {
      // Updating direct field
      const updatedData = { ...influencersData, [field]: indexOrValue };
      setInfluencersData(updatedData);
      saveDataToFirebase(planId, 'influencers', updatedData);
    }
  };

  const saveRoadmapData = (field: string, indexOrValue: number | string, subField?: string, value?: any) => {
    if (!planId) return;
    
    if (typeof indexOrValue === 'number' && subField && value !== undefined) {
      // Updating array item field
      const updatedData = { ...roadmapData };
      if (field === 'milestones' && updatedData.milestones) {
        updatedData.milestones = [...updatedData.milestones];
        updatedData.milestones[indexOrValue] = { ...updatedData.milestones[indexOrValue], [subField]: value };
      }
      setRoadmapData(updatedData);
      saveDataToFirebase(planId, 'roadmap', updatedData);
    } else if (typeof indexOrValue === 'string') {
      // Updating direct field
      const updatedData = { ...roadmapData, [field]: indexOrValue };
      setRoadmapData(updatedData);
      saveDataToFirebase(planId, 'roadmap', updatedData);
    }
  };

  const saveExecutionData = (field: string, value: any) => {
    if (!planId) return;
    const updatedData = { ...executionData, [field]: value };
    setExecutionData(updatedData);
    saveDataToFirebase(planId, 'execution', updatedData);
  };

  // Function to save update-task assignments
  const saveUpdateTaskAssignment = async (updateType: string, taskType: string, assigned: boolean) => {
    if (!planId) return;
    
    const updatedData = { ...executionData };
    if (!updatedData.updateTaskAssignments) {
      updatedData.updateTaskAssignments = {};
    }
    if (!updatedData.updateTaskAssignments[updateType]) {
      updatedData.updateTaskAssignments[updateType] = [];
    }
    
    if (assigned) {
      // Add task if not already assigned
      if (!updatedData.updateTaskAssignments[updateType].includes(taskType)) {
        updatedData.updateTaskAssignments[updateType].push(taskType);
      }
    } else {
      // Remove task if assigned
      updatedData.updateTaskAssignments[updateType] = updatedData.updateTaskAssignments[updateType].filter(
        (task: string) => task !== taskType
      );
    }
    
    setExecutionData(updatedData);
    await saveDataToFirebase(planId, 'execution', updatedData);
  };

  // Load data from Firebase subcollections
  const loadMarketingData = async (planId: string) => {
    setLoading(true);
    try {
      const [dashboard, strategy, plan, influencers, roadmap, execution, updates, tasks, plans] = await Promise.all([
        readMarketingDataFromFirebase(planId, 'dashboard'),
        readMarketingDataFromFirebase(planId, 'strategy'),
        readMarketingDataFromFirebase(planId, 'plan'),
        readMarketingDataFromFirebase(planId, 'influencers'),
        readMarketingDataFromFirebase(planId, 'roadmap'),
        readMarketingDataFromFirebase(planId, 'execution'),
        readSettingsData('marketingupdates'),
        readSettingsData('marketingtasks'),
        getAllMarketingPlans()
      ]);

      setDashboardData(dashboard);
      setStrategyData(strategy);
      setPlanData(plan);
      setInfluencersData(influencers);
      setRoadmapData(roadmap);
      setExecutionData(execution);
      setMarketingUpdates(updates?.types || []);
      setMarketingTasks(tasks?.types || []);
      setMarketingPlans(plans);
    } catch (error) {
      console.error('Error loading marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load plan data when plan is selected
  useEffect(() => {
    if (planId) {
      loadMarketingData(planId);
    }
  }, [planId]);

  // Cleanup Chart.js instances on unmount or view change
  useEffect(() => {
    return () => {
      // Destroy any existing Chart.js instances to prevent canvas reuse errors
      const charts = ChartJS.getChart('budget-allocation-chart');
      if (charts) {
        charts.destroy();
      }
      const kpiChart = ChartJS.getChart('kpi-chart');
      if (kpiChart) {
        kpiChart.destroy();
      }
    };
  }, [view]); // Re-run when view changes

  const calculateDashboard = (planData: any) => {
    // ...
  };

  // Handle plans dropdown
  const handlePlansClick = (event: React.MouseEvent<HTMLElement>) => {
    setPlansAnchorEl(event.currentTarget);
  };

  const handlePlansClose = () => {
    setPlansAnchorEl(null);
  };

  const handlePlanSelect = (selectedPlan: any, phaseIndex: number, weekIndex: number) => {
    // Add the selected plan as an action (push to array)
    console.log('selectedPlan', selectedPlan);
    console.log('planData', planData);
    console.log('phaseIndex', phaseIndex, 'weekIndex', weekIndex);
    
    if (!planData || !planData.phases || !planData.phases[phaseIndex] || !planData.phases[phaseIndex].weeks || !planData.phases[phaseIndex].weeks[weekIndex]) {
      console.error('Invalid planData structure or indices');
      return;
    }
    
    const newPlanData = JSON.parse(JSON.stringify(planData));
    // Ensure actions is an array
    if (!Array.isArray(newPlanData.phases[phaseIndex].weeks[weekIndex].actions)) {
      newPlanData.phases[phaseIndex].weeks[weekIndex].actions = [];
    }
    newPlanData.phases[phaseIndex].weeks[weekIndex].actions.push({
      text: `[PLAN] ${selectedPlan.name}`,
      planId: selectedPlan.id,
      planData: selectedPlan
    });
    console.log('newPlanData', newPlanData);
    console.log('Saving to Firebase with planId:', planId);
    setPlanData(newPlanData);
    saveDataToFirebase(planId, 'plan', newPlanData);
    handlePlansClose();
  };

  if (loading) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Loading marketing data...</Typography>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">        
        <AppBar position="sticky" sx={{ top: 16, borderRadius: 2, mb: 4, zIndex: 50 }}>
          <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button 
                variant="text" 
                onClick={onBackToPlans}
                sx={{ color: 'white' }}
              >
                ← Back to Plans
              </Button>
              <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
                {planName}
              </Typography>
              <VotingSystem 
                collectionName="marketingExecution" 
                documentId={planId} 
                size="small"
                showCounts={true}
                showTooltips={true}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant={view === 'dashboard' ? 'contained' : 'text'} 
                onClick={() => handleNavClick('dashboard')}
                sx={{ color: view === 'dashboard' ? 'white' : 'inherit' }}
              >
                Dashboard
              </Button>
              <Button 
                variant={view === 'strategy' ? 'contained' : 'text'} 
                onClick={() => handleNavClick('strategy')}
                sx={{ color: view === 'strategy' ? 'white' : 'inherit' }}
              >
                Strategy
              </Button>
              <Button 
                variant={view === 'plan' ? 'contained' : 'text'} 
                onClick={() => handleNavClick('plan')}
                sx={{ color: view === 'plan' ? 'white' : 'inherit' }}
              >
                The Plan
              </Button>
              <Button 
                variant={view === 'influencers' ? 'contained' : 'text'} 
                onClick={() => handleNavClick('influencers')}
                sx={{ color: view === 'influencers' ? 'white' : 'inherit' }}
              >
                Influencers
              </Button>
              <Button 
                variant={view === 'roadmap' ? 'contained' : 'text'} 
                onClick={() => handleNavClick('roadmap')}
                sx={{ color: view === 'roadmap' ? 'white' : 'inherit' }}
              >
                Dev Roadmap
              </Button>
              <Button 
                variant={view === 'execution' ? 'contained' : 'text'} 
                onClick={() => handleNavClick('execution')}
                sx={{ color: view === 'execution' ? 'white' : 'inherit' }}
              >
                Execution Framework
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        
        <main>
          {view === 'dashboard' && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={dashboardData?.title || 'Executive Summary'} 
                        onSave={(newValue) => {
                          const updatedData = { ...dashboardData, title: newValue };
                          setDashboardData(updatedData);
                          saveDataToFirebase(planId, 'dashboard', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      <EditableText 
                        value={dashboardData?.description || 'This report presents a comprehensive, six-month marketing strategy for the free-to-play (F2P) digital trading card game (TCG), The Lost Glitches. With a budget of $60,000 USD, this plan is designed to leverage the upcoming major game update and the subsequent launch of competitive tournaments to fundamentally reset the game\'s market position, cultivate a dedicated community, and establish a sustainable growth trajectory. The current situation, characterized by a well-liked but undiscovered product hampered by a negative perception from a past association with Web3 technologies, necessitates a decisive and multi-faceted marketing intervention.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...dashboardData, description: newValue };
                          setDashboardData(updatedData);
                          saveDataToFirebase(planId, 'dashboard', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h5" component="h3" gutterBottom>
                      Plan Duration
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Start Date
                        </Typography>
                        <TextField
                          type="date"
                          value={dashboardData?.duration?.start?.toDate?.()?.toISOString().split('T')[0] || ''}
                          onChange={(e) => {
                            const newDate = new Date(e.target.value);
                            const updatedData = { ...dashboardData };
                            if (!updatedData.duration) updatedData.duration = {};
                            updatedData.duration.start = newDate;
                            setDashboardData(updatedData);
                            saveDataToFirebase(planId, 'dashboard', updatedData);
                          }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          End Date
                        </Typography>
                        <TextField
                          type="date"
                          value={dashboardData?.duration?.end?.toDate?.()?.toISOString().split('T')[0] || ''}
                          onChange={(e) => {
                            const newDate = new Date(e.target.value);
                            const updatedData = { ...dashboardData };
                            if (!updatedData.duration) updatedData.duration = {};
                            updatedData.duration.end = newDate;
                            setDashboardData(updatedData);
                            saveDataToFirebase(planId, 'dashboard', updatedData);
                          }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Card elevation={2} sx={{ textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary">Total Budget</Typography>
                    <Typography variant="h3" component="p" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1 }}>
                      {formatCurrency(budgetData.totalBudget)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Card elevation={2} sx={{ textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary">New Installs Goal</Typography>
                    <Box sx={{ mt: 1 }}>
                      <EditableText 
                        value={dashboardData?.goals?.newInstallsGoal?.toString() || ''} 
                        onSave={(newValue) => saveDashboardGoals('newInstallsGoal', newValue)} 
                        variant="h3"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h5" component="h3" align="center" gutterBottom>
                      Budget Allocation
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }} key={`budget-chart-${view}`}>
                      {budgetData.budgetAllocation.length > 0 ? (
                        <Doughnut 
                          data={doughnutChartData} 
                          options={{ responsive: true, maintainAspectRatio: false }}
                          id="budget-allocation-chart"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          No budget data available
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">KPIs: Projection vs. Result</Typography>
                    <Box key={`kpi-chart-${view}`}>
                      {phaseLabels.length > 0 ? (
                        <Line 
                          data={lineChartData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true
                              }
                            }
                          }}
                          id="kpi-chart"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          No KPI data available
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>


            </Grid>
          )}
          {view === 'strategy' && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={strategyData?.title || 'Situational Analysis'} 
                        onSave={(newValue) => {
                          const updatedData = { ...strategyData, title: newValue };
                          setStrategyData(updatedData);
                          saveDataToFirebase(planId, 'strategy', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={strategyData?.description || 'The Lost Glitches exists in a challenging market position. While the game has received positive feedback from players who have discovered it, the combination of a small player base and lingering negative associations with Web3/NFTs has created a significant barrier to growth. This analysis examines the current situation and identifies the strategic opportunities for repositioning the game.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...strategyData, description: newValue };
                          setStrategyData(updatedData);
                          saveDataToFirebase(planId, 'strategy', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {strategyData?.swot?.map((item: any, index: number) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Card sx={{ borderLeft: `4px solid ${item.color}`, height: '100%' }}>
                            <CardContent>
                              <EditableText 
                                value={item.title} 
                                onSave={(newValue) => saveStrategyData('swot', index, 'title', newValue)} 
                                variant="h6"
                                sx={{ mb: 2 }}
                              />
                              <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                {item.items.map((text: string, i: number) => (
                                  <li key={i} style={{ marginBottom: '8px' }}>
                                    <EditableArrayItem 
                                      value={text} 
                                      onSave={(newValue) => {
                                        const updatedItems = [...item.items];
                                        updatedItems[i] = newValue;
                                        saveStrategyData('swot', index, 'items', updatedItems);
                                      }}
                                      onDelete={() => {
                                        const updatedItems = item.items.filter((_: string, itemIndex: number) => itemIndex !== i);
                                        saveStrategyData('swot', index, 'items', updatedItems);
                                      }}
                                      variant="body2"
                                    />
                                  </li>
                                ))}
                              </ul>
                              <AddItemButton 
                                onAdd={() => {
                                  const updatedItems = [...item.items, 'New SWOT item'];
                                  saveStrategyData('swot', index, 'items', updatedItems);
                                }}
                                label="SWOT Item"
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                      {(!strategyData?.swot || strategyData.swot.length === 0) && (
                        <Grid item xs={12}>
                          <Card sx={{ border: '2px dashed', borderColor: 'divider', p: 3, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No SWOT Analysis Items
                            </Typography>
                            <Button 
                              variant="outlined" 
                              onClick={() => {
                                const defaultSwot = [
                                  { title: 'Strengths', color: '#4CAF50', items: ['Strong game mechanics', 'Positive player feedback', 'Unique art style'] },
                                  { title: 'Weaknesses', color: '#F44336', items: ['Small player base', 'Limited marketing budget', 'Web3 association stigma'] },
                                  { title: 'Opportunities', color: '#2196F3', items: ['Major game update', 'Tournament launch', 'Growing TCG market'] },
                                  { title: 'Threats', color: '#FF9800', items: ['Competition from established games', 'Market saturation', 'Economic uncertainty'] }
                                ];
                                const updatedData = { ...strategyData, swot: defaultSwot };
                                setStrategyData(updatedData);
                                saveDataToFirebase(planId, 'strategy', updatedData);
                              }}
                            >
                              Populate SWOT Analysis
                            </Button>
                          </Card>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={strategyData?.toolkitTitle || 'Marketing Toolkit'} 
                        onSave={(newValue) => {
                          const updatedData = { ...strategyData, toolkitTitle: newValue };
                          setStrategyData(updatedData);
                          saveDataToFirebase(planId, 'strategy', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={strategyData?.toolkitDescription || 'Our marketing strategy employs four primary tools, each serving a specific purpose in our overall campaign. This toolkit is designed to work synergistically, with each element supporting and amplifying the others.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...strategyData, toolkitDescription: newValue };
                          setStrategyData(updatedData);
                          saveDataToFirebase(planId, 'strategy', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Box sx={{ mt: 4 }}>
                      {strategyData?.toolkit?.map((item: any, index: number) => (
                        <Card key={index} sx={{ borderLeft: `4px solid ${item.color}`, mb: 2 }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <EditableText 
                                value={item.title} 
                                onSave={(newValue) => saveStrategyData('toolkit', index, 'title', newValue)} 
                                variant="h6"
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedToolkit = strategyData.toolkit.filter((_: any, i: number) => i !== index);
                                  const updatedData = { ...strategyData, toolkit: updatedToolkit };
                                  setStrategyData(updatedData);
                                  saveDataToFirebase(planId, 'strategy', updatedData);
                                }}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { 
                                    backgroundColor: 'error.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <EditableText 
                              value={item.description} 
                              onSave={(newValue) => saveStrategyData('toolkit', index, 'description', newValue)} 
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            />
                          </CardContent>
                        </Card>
                      ))}
                      {(!strategyData?.toolkit || strategyData.toolkit.length === 0) && (
                        <Card sx={{ border: '2px dashed', borderColor: 'divider', p: 3, textAlign: 'center' }}>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Marketing Toolkit Items
                          </Typography>
                          <Button 
                            variant="outlined" 
                            onClick={() => {
                              const defaultToolkit = [
                                { title: 'Influencer Marketing', color: '#4CAF50', description: 'Partner with gaming influencers to reach our target audience and build credibility.' },
                                { title: 'Paid Advertising', color: '#2196F3', description: 'Use targeted ads on social media and gaming platforms to drive user acquisition.' },
                                { title: 'Community Building', color: '#FF9800', description: 'Foster an engaged community through Discord, events, and content creation.' },
                                { title: 'Content Marketing', color: '#9C27B0', description: 'Create valuable content that showcases the game and attracts organic traffic.' }
                              ];
                              const updatedData = { ...strategyData, toolkit: defaultToolkit };
                              setStrategyData(updatedData);
                              saveDataToFirebase(planId, 'strategy', updatedData);
                            }}
                          >
                            Populate Marketing Toolkit
                          </Button>
                        </Card>
                      )}
                      {strategyData?.toolkit && strategyData.toolkit.length > 0 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Button 
                            variant="outlined" 
                            onClick={() => {
                              const newToolkit = {
                                title: 'New Marketing Tool',
                                color: '#6B7280',
                                description: 'Description of this marketing tool and its purpose.'
                              };
                              const updatedToolkit = [...(strategyData.toolkit || []), newToolkit];
                              const updatedData = { ...strategyData, toolkit: updatedToolkit };
                              setStrategyData(updatedData);
                              saveDataToFirebase(planId, 'strategy', updatedData);
                            }}
                            startIcon={<AddIcon />}
                          >
                            Add Marketing Tool
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={strategyData?.personasTitle || 'Player Personas'} 
                        onSave={(newValue) => {
                          const updatedData = { ...strategyData, personasTitle: newValue };
                          setStrategyData(updatedData);
                          saveDataToFirebase(planId, 'strategy', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={strategyData?.personasDescription || 'Understanding our target audience is crucial for effective marketing. These personas represent the three primary player archetypes we\'re targeting, based on traditional TCG player psychology and our game\'s unique positioning.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...strategyData, personasDescription: newValue };
                          setStrategyData(updatedData);
                          saveDataToFirebase(planId, 'strategy', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      {strategyData?.personas?.map((persona: any, index: number) => (
                        <Grid item xs={12} md={4} key={index}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <EditableText 
                                  value={persona.name} 
                                  onSave={(newValue) => saveStrategyData('personas', index, 'name', newValue)} 
                                  variant="h6"
                                  sx={{ flex: 1 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updatedPersonas = strategyData.personas.filter((_: any, i: number) => i !== index);
                                    const updatedStrategyData = { ...strategyData, personas: updatedPersonas };
                                    setStrategyData(updatedStrategyData);
                                    saveDataToFirebase(planId, 'strategy', updatedStrategyData);
                                  }}
                                  sx={{ 
                                    color: 'error.main',
                                    '&:hover': { 
                                      backgroundColor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                
                                <EditableText 
                                  value={persona.motivation} 
                                  onSave={(newValue) => saveStrategyData('personas', index, 'motivation', newValue)} 
                                  variant="body2"
                                  sx={{ color: 'text.secondary' }}
                                />
                                
                              </Box>
                              <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold' }}>Pain Points:</Typography>
                              <EditableText 
                                value={persona.painPoints} 
                                onSave={(newValue) => saveStrategyData('personas', index, 'painPoints', newValue)} 
                                variant="body2"
                                sx={{ color: 'text.secondary', mb: 2 }}
                              />
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Hooks:</Typography>
                              <EditableText 
                                value={persona.hooks} 
                                onSave={(newValue) => saveStrategyData('personas', index, 'hooks', newValue)} 
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                      {(!strategyData?.personas || strategyData.personas.length === 0) && (
                        <Grid item xs={12}>
                          <Card sx={{ border: '2px dashed', borderColor: 'divider', p: 3, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No Player Personas
                            </Typography>
                            <Button 
                              variant="outlined" 
                              onClick={() => {
                                const defaultPersonas = [
                                  { 
                                    name: 'The Competitive Player', 
                                    motivation: 'Seeks to dominate and climb rankings through strategic gameplay and optimization.',
                                    painPoints: 'Limited competitive scene, lack of high-level content, poor matchmaking.',
                                    hooks: 'Tournament announcements, meta analysis, pro player content.'
                                  },
                                  { 
                                    name: 'The Collector', 
                                    motivation: 'Driven by completion and rarity, enjoys building comprehensive card collections.',
                                    painPoints: 'Expensive rare cards, limited trading options, storage management.',
                                    hooks: 'New card reveals, limited edition releases, collection showcases.'
                                  },
                                  { 
                                    name: 'The Casual Social Player', 
                                    motivation: 'Plays for fun and social interaction, enjoys the community aspect.',
                                    painPoints: 'Toxic players, complex mechanics, time investment required.',
                                    hooks: 'Community events, beginner-friendly content, social features.'
                                  }
                                ];
                                const updatedData = { ...strategyData, personas: defaultPersonas };
                                setStrategyData(updatedData);
                                saveDataToFirebase(planId, 'strategy', updatedData);
                              }}
                            >
                              Populate Player Personas
                            </Button>
                          </Card>
                        </Grid>
                      )}
                      {strategyData?.personas && strategyData.personas.length > 0 && (
                        <Grid item xs={12}>
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Button 
                              variant="outlined" 
                              onClick={() => {
                                const newPersona = {
                                  name: 'New Player Persona',
                                  motivation: 'Describe what motivates this player type.',
                                  painPoints: 'Describe the main pain points for this player type.',
                                  hooks: 'Describe what content or features would hook this player type.'
                                };
                                const updatedPersonas = [...(strategyData.personas || []), newPersona];
                                const updatedData = { ...strategyData, personas: updatedPersonas };
                                setStrategyData(updatedData);
                                saveDataToFirebase(planId, 'strategy', updatedData);
                              }}
                              startIcon={<AddIcon />}
                            >
                              Add Player Persona
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {view === 'plan' && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={planData?.title || '6-Month Action Plan'} 
                        onSave={(newValue) => {
                          const updatedData = { ...planData, title: newValue };
                          setPlanData(updatedData);
                          saveDataToFirebase(planId, 'plan', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={planData?.description || 'This detailed action plan breaks down our marketing strategy into three distinct phases, each with specific objectives, activities, and budget allocations. The plan is designed to be flexible and responsive to performance data, allowing for optimization based on real-world results.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...planData, description: newValue };
                          setPlanData(updatedData);
                          saveDataToFirebase(planId, 'plan', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      <EditableText value={planData?.subtitle || ''} onSave={(newValue) => saveDataToFirebase(planId, 'plan', { ...planData, subtitle: newValue })} variant="h6" />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      <EditableText value={planData?.subDescription || ''} onSave={(newValue) => saveDataToFirebase(planId, 'plan', { ...planData, subDescription: newValue })} variant="body1" />
                    </Typography>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs value={activePhase} onChange={(e, newValue) => setActivePhase(newValue)} aria-label="plan phases" variant="scrollable" scrollButtons="auto">
                        {planData.phases?.map((phase: any, index: number) => (
                          <Tab 
                            key={index}
                            label={phase.title || phase.phase || `Phase ${index + 1}`}
                            id={`phase-tab-${index}`} 
                            aria-controls={`phase-tabpanel-${index}`} 
                          />
                        ))}
                      </Tabs>
                    </Box>

                    {(!planData?.phases || planData.phases.length === 0) && (
                      <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Card sx={{ border: '2px dashed', borderColor: 'divider', p: 3 }}>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Plan Phases
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Create a structured action plan with phases and weekly activities.
                          </Typography>
                          <Button 
                            variant="outlined" 
                            onClick={() => {
                              const defaultPhases = [
                                { 
                                  title: 'Phase 1: Foundation & Awareness', 
                                  weeks: [
                                    { 
                                      title: 'Week 1-2', 
                                      actions: {
                                        action_1: { text: 'Set up social media accounts and branding', projection: 500, result: 0 }
                                      }, 
                                      spending: {} 
                                    },
                                    { 
                                      title: 'Week 3-4', 
                                      actions: {
                                        action_2: { text: 'Launch initial influencer partnerships', projection: 500, result: 0 }
                                      }, 
                                      spending: {} 
                                    }
                                  ]
                                },
                                { 
                                  title: 'Phase 2: Growth & Engagement', 
                                  weeks: [
                                    { 
                                      title: 'Week 5-6', 
                                      actions: {
                                        action_3: { text: 'Implement paid advertising campaigns', projection: 1250, result: 0 }
                                      }, 
                                      spending: {} 
                                    },
                                    { 
                                      title: 'Week 7-8', 
                                      actions: {
                                        action_4: { text: 'Host community events and tournaments', projection: 1250, result: 0 }
                                      }, 
                                      spending: {} 
                                    }
                                  ]
                                },
                                { 
                                  title: 'Phase 3: Scale & Optimization', 
                                  weeks: [
                                    { 
                                      title: 'Week 9-10', 
                                      actions: {
                                        action_5: { text: 'Analyze performance data and optimize campaigns', projection: 1250, result: 0 }
                                      }, 
                                      spending: {} 
                                    },
                                    { 
                                      title: 'Week 11-12', 
                                      actions: {
                                        action_6: { text: 'Scale successful channels and launch new initiatives', projection: 1250, result: 0 }
                                      }, 
                                      spending: {} 
                                    }
                                  ]
                                }
                              ];
                              const updatedPlanData = { ...planData, phases: defaultPhases };
                              setPlanData(updatedPlanData);
                              saveDataToFirebase(planId, 'plan', updatedPlanData);
                            }}
                          >
                            Populate Plan Phases
                          </Button>
                        </Card>
                      </Box>
                    )}

                    {planData.phases?.map((phase: any, phaseIndex: number) => (
                      <TabPanel value={activePhase} index={phaseIndex} key={phaseIndex}>
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            <EditableText 
                                value={phase.title || phase.phase || `Phase ${phaseIndex + 1}`}
                                onSave={(newValue) => {
                                    const newPlanData = JSON.parse(JSON.stringify(planData));
                                    newPlanData.phases[phaseIndex].title = newValue;
                                    if (newPlanData.phases[phaseIndex].phase) {
                                      delete newPlanData.phases[phaseIndex].phase; // Migrate old property
                                    }
                                    setPlanData(newPlanData);
                                    saveDataToFirebase(planId, 'plan', newPlanData);
                                }} 
                                variant="h6" 
                            />
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Projection (installs)</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {(() => {
                                        let totalProjection = 0;
                                        if (phase.weeks) {
                                            phase.weeks.forEach((week: any) => {
                                                if (week.actions && typeof week.actions === 'object') {
                                                    Object.values(week.actions).forEach((action: any) => {
                                                        totalProjection += action.projection || 0;
                                                    });
                                                }
                                            });
                                        }
                                        return totalProjection.toLocaleString();
                                    })()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Result (installs)</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                    {(() => {
                                        let totalResult = 0;
                                        if (phase.weeks) {
                                            phase.weeks.forEach((week: any) => {
                                                if (week.actions && typeof week.actions === 'object') {
                                                    Object.values(week.actions).forEach((action: any) => {
                                                        totalResult += action.result || 0;
                                                    });
                                                }
                                            });
                                        }
                                        return totalResult.toLocaleString();
                                    })()}
                                </Typography>
                            </Box>
                        </Box>
                      
                        {phase.weeks?.map((week: any, weekIndex: number) => (
                          <Accordion key={weekIndex} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 2 }}>
                                <EditableText value={week.title} onSave={(newValue) => {
                                  const newPlanData = JSON.parse(JSON.stringify(planData));
                                  newPlanData.phases[phaseIndex].weeks[weekIndex].title = newValue;
                                  setPlanData(newPlanData);
                                  saveDataToFirebase(planId, 'plan', newPlanData);
                                }} variant="body1" />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newPlanData = JSON.parse(JSON.stringify(planData));
                                    newPlanData.phases[phaseIndex].weeks.splice(weekIndex, 1);
                                    setPlanData(newPlanData);
                                    saveDataToFirebase(planId, 'plan', newPlanData);
                                  }}
                                  sx={{ '&:hover': { color: 'error.main' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                                                    <Typography variant="subtitle1" gutterBottom>Actions:</Typography>
                                  {getActionsArray(week.actions).map((action: any, actionIndex: number) => (
                                    <Box key={actionIndex} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Action
                                          </Typography>
                                          <EditableText
                                            value={action.text || (typeof action === 'string' ? action : '')}
                                            onSave={(newValue) => {
                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                              // Update the text for the action at the correct index
                                              if (typeof newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex] === 'string') {
                                                newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex] = newValue;
                                              } else {
                                                newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex].text = newValue;
                                              }
                                              setPlanData(newPlanData);
                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                            }}
                                            placeholder="Enter action description"
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Projection (installs)
                                          </Typography>
                                          <EditableNumber
                                            value={action.projection || 0}
                                            onSave={(newValue) => {
                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                              if (typeof newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex] !== 'string') {
                                                newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex].projection = newValue;
                                              }
                                              setPlanData(newPlanData);
                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                            }}
                                            formatCurrency={false}
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Result (installs)
                                          </Typography>
                                          <EditableNumber
                                            value={action.result || 0}
                                            onSave={(newValue) => {
                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                              if (typeof newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex] !== 'string') {
                                                newPlanData.phases[phaseIndex].weeks[weekIndex].actions[actionIndex].result = newValue;
                                              }
                                              setPlanData(newPlanData);
                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                            }}
                                            formatCurrency={false}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const newPlanData = JSON.parse(JSON.stringify(planData));
                                            newPlanData.phases[phaseIndex].weeks[weekIndex].actions.splice(actionIndex, 1);
                                            setPlanData(newPlanData);
                                            saveDataToFirebase(planId, 'plan', newPlanData);
                                          }}
                                          sx={{ '&:hover': { color: 'error.main' } }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  ))}
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <AddItemButton onAdd={() => {
                                      const newPlanData = JSON.parse(JSON.stringify(planData));
                                      if (!Array.isArray(newPlanData.phases[phaseIndex].weeks[weekIndex].actions)) {
                                        newPlanData.phases[phaseIndex].weeks[weekIndex].actions = [];
                                      }
                                      newPlanData.phases[phaseIndex].weeks[weekIndex].actions.push({
                                        text: 'New Action',
                                        projection: 0,
                                        result: 0
                                      });
                                      setPlanData(newPlanData);
                                      saveDataToFirebase(planId, 'plan', newPlanData);
                                    }} label="Action" />
                                    
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={(e) => {
                                        setPlansAnchorEl(e.currentTarget);
                                        setCurrentPlanIndices({ phaseIndex, weekIndex });
                                      }}
                                      startIcon={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>+</Typography>}
                                      sx={{ 
                                        borderStyle: 'dashed',
                                        color: 'text.secondary',
                                        '&:hover': { 
                                          borderStyle: 'solid',
                                          color: 'primary.main',
                                          borderColor: 'primary.main'
                                        }
                                      }}
                                    >
                                      Add Plan
                                    </Button>
                                    <Menu
                                      anchorEl={plansAnchorEl}
                                      open={Boolean(plansAnchorEl)}
                                      onClose={handlePlansClose}
                                    >
                                      {marketingPlans.map((plan) => (
                                        <MenuItem key={plan.id} onClick={() => {
                                          if (currentPlanIndices) {
                                            handlePlanSelect(plan, currentPlanIndices.phaseIndex, currentPlanIndices.weekIndex);
                                          }
                                        }}>
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                              {plan.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Budget: ${plan.expectedBudget?.toLocaleString() || 0}
                                            </Typography>
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Menu>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Spending</Typography>
                                  <Box>
                                      {week.spending && typeof week.spending === 'object' && !Array.isArray(week.spending) && Object.keys(week.spending).length > 0 ? (
                                          <>
                                              {Object.entries(week.spending)
                                                  .filter(([category]) => category !== 'Total' && category !== 'Internal' && category !== 'External' && category !== 'Partner')
                                                  .map(([category, spendingItem]: [string, any]) => {
                                                    // Convert legacy number to object
                                                    if (typeof spendingItem === 'number') {
                                                      spendingItem = { amount: spendingItem, type: 'internal' };
                                                      const newPlanData = JSON.parse(JSON.stringify(planData));
                                                      newPlanData.phases[phaseIndex].weeks[weekIndex].spending[category] = spendingItem;
                                                      setPlanData(newPlanData);
                                                      saveDataToFirebase(planId, 'plan', newPlanData);
                                                    }
                                                    return (
                                                      <Box key={category} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 0 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                          <EditableText
                                                            value={category}
                                                            onSave={(newCategory) => {
                                                              if (newCategory === category || !newCategory) return;
                                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                                              const spending = newPlanData.phases[phaseIndex].weeks[weekIndex].spending;
                                                              const currentItem = spending[category];
                                                              delete spending[category];
                                                              spending[newCategory] = currentItem;
                                                              setPlanData(newPlanData);
                                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                                            }}
                                                            placeholder="Category"
                                                            sx={{ minWidth: 180, maxWidth: 180, flexShrink: 0, mr: 2 }}
                                                          />
                                                          <EditableNumber
                                                            value={spendingItem.amount || 0}
                                                            onSave={(newAmount) => {
                                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                                              const spending = newPlanData.phases[phaseIndex].weeks[weekIndex].spending;
                                                              if (typeof spending[category] === 'number') {
                                                                spending[category] = { amount: spending[category], type: 'internal' };
                                                              }
                                                              spending[category].amount = Number(newAmount);
                                                              const totals = calculateSpendingTotals(spending);
                                                              spending.Internal = totals.internal;
                                                              spending.External = totals.external;
                                                              spending.Partner = totals.partner;
                                                              spending.Total = totals.total;
                                                              setPlanData(newPlanData);
                                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                                            }}
                                                            formatCurrency={true}
                                                            sx={{ minWidth: 100, maxWidth: 100, flexShrink: 0, mr: 2 }}
                                                          />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, justifyContent: 'flex-end', flex: 0 }}>
                                                          <EditableChip
                                                            value={spendingItem.type || 'internal'}
                                                            options={[
                                                              { value: 'internal', label: 'Internal' },
                                                              { value: 'external', label: 'External' },
                                                              { value: 'partner', label: 'Partner' }
                                                            ]}
                                                            onSave={(newType) => {
                                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                                              const spending = newPlanData.phases[phaseIndex].weeks[weekIndex].spending;
                                                              if (typeof spending[category] === 'number') {
                                                                spending[category] = { amount: spending[category], type: 'internal' };
                                                              }
                                                              spending[category].type = newType;
                                                              const totals = calculateSpendingTotals(spending);
                                                              spending.Internal = totals.internal;
                                                              spending.External = totals.external;
                                                              spending.Partner = totals.partner;
                                                              spending.Total = totals.total;
                                                              setPlanData(newPlanData);
                                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                                            }}
                                                            getColor={(value) => {
                                                              switch (value) {
                                                                case 'internal': return 'primary';
                                                                case 'external': return 'error';
                                                                case 'partner': return 'success';
                                                                default: return 'default';
                                                              }
                                                            }}
                                                          />
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                              const newPlanData = JSON.parse(JSON.stringify(planData));
                                                              const spending = newPlanData.phases[phaseIndex].weeks[weekIndex].spending;
                                                              delete spending[category];
                                                              const totals = calculateSpendingTotals(spending);
                                                              spending.Internal = totals.internal;
                                                              spending.External = totals.external;
                                                              spending.Partner = totals.partner;
                                                              spending.Total = totals.total;
                                                              setPlanData(newPlanData);
                                                              saveDataToFirebase(planId, 'plan', newPlanData);
                                                            }}
                                                            sx={{ ml: 1 }}
                                                          >
                                                            <DeleteIcon fontSize="small" />
                                                          </IconButton>
                                                        </Box>
                                                      </Box>
                                                    );
                                                  })}
                                              {/* Subtotals and total, plain and left-aligned */}
                                              <Box sx={{ mt: 2, fontSize: 14, color: 'text.secondary', fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <span>Internal: ${Number(week.spending.Internal || 0).toLocaleString()}</span>
                                                <span>External: ${Number(week.spending.External || 0).toLocaleString()}</span>
                                                <span>Partner: ${Number(week.spending.Partner || 0).toLocaleString()}</span>
                                                <span style={{ fontWeight: 700 }}>TOTAL: ${Number(week.spending.Total || 0).toLocaleString()}</span>
                                              </Box>
                                              <AddItemButton
                                                  onAdd={() => {
                                                      const newPlanData = JSON.parse(JSON.stringify(planData));
                                                      const week = newPlanData.phases[phaseIndex].weeks[weekIndex];
                                                      let newCategoryName = 'New Category';
                                                      let counter = 1;
                                                      while (week.spending.hasOwnProperty(newCategoryName)) {
                                                          newCategoryName = `New Category ${counter++}`;
                                                      }
                                                      week.spending[newCategoryName] = {
                                                          amount: 0,
                                                          type: 'internal'
                                                      };
                                                      const totals = calculateSpendingTotals(week.spending);
                                                      week.spending.Internal = totals.internal;
                                                      week.spending.External = totals.external;
                                                      week.spending.Partner = totals.partner;
                                                      week.spending.Total = totals.total;
                                                      setPlanData(newPlanData);
                                                      saveDataToFirebase(planId, 'plan', newPlanData);
                                                  }}
                                                  label="Spending Category"
                                              />
                                          </>
                                      ) : (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                              <Typography variant="body2">No direct spend this period.</Typography>
                                              <AddItemButton
                                                  onAdd={() => {
                                                      const newPlanData = JSON.parse(JSON.stringify(planData || { phases: [] }));
                                                      if (!newPlanData.phases) newPlanData.phases = [];
                                                      while (newPlanData.phases.length <= phaseIndex) {
                                                          newPlanData.phases.push({ title: `Phase ${newPlanData.phases.length + 1}`, weeks: [] });
                                                      }
                                                      if (!newPlanData.phases[phaseIndex].weeks) {
                                                          newPlanData.phases[phaseIndex].weeks = [];
                                                      }
                                                      while (newPlanData.phases[phaseIndex].weeks.length <= weekIndex) {
                                                          newPlanData.phases[phaseIndex].weeks.push({ actions: {}, spending: {} });
                                                      }
                                                      const week = newPlanData.phases[phaseIndex].weeks[weekIndex];
                                                      week.spending = {
                                                          'New Category': {
                                                              amount: 0,
                                                              type: 'internal'
                                                          },
                                                          'Internal': 0,
                                                          'External': 0,
                                                          'Partner': 0,
                                                          'Total': 0
                                                      };
                                                      setPlanData(newPlanData);
                                                      saveDataToFirebase(planId, 'plan', newPlanData);
                                                  }}
                                                  label="Spending Category"
                                              />
                                          </Box>
                                      )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                        <Button
                            variant="outlined"
                            onClick={() => {
                                const newPlanData = JSON.parse(JSON.stringify(planData));
                                if (!newPlanData.phases[phaseIndex].weeks) {
                                    newPlanData.phases[phaseIndex].weeks = [];
                                }
                                newPlanData.phases[phaseIndex].weeks.push({ title: `Week ${newPlanData.phases[phaseIndex].weeks.length * 2 + 1}-${newPlanData.phases[phaseIndex].weeks.length * 2 + 2}`, actions: [], spending: {} });
                                setPlanData(newPlanData);
                                saveDataToFirebase(planId, 'plan', newPlanData);
                            }}
                            sx={{ mt: 2 }}
                        >
                            Add Week
                        </Button>
                     </TabPanel>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {view === 'influencers' && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={influencersData?.title || 'Influencer Strategy'} 
                        onSave={(newValue) => {
                          const updatedData = { ...influencersData, title: newValue };
                          setInfluencersData(updatedData);
                          saveDataToFirebase(planId, 'influencers', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={influencersData?.description || 'Our influencer marketing strategy targets three tiers of creators, each serving a specific purpose in our overall campaign. We\'ve identified creators who align with our target audience and can authentically represent our game.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...influencersData, description: newValue };
                          setInfluencersData(updatedData);
                          saveDataToFirebase(planId, 'influencers', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 4 }}>
                      <EditableText 
                        value={influencersData?.youtubeTwitchTitle || 'YouTube & Twitch Influencers'} 
                        onSave={(newValue) => {
                          const updatedData = { ...influencersData, youtubeTwitchTitle: newValue };
                          setInfluencersData(updatedData);
                          saveDataToFirebase(planId, 'influencers', updatedData);
                        }} 
                        variant="h5"
                      />
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {influencersData?.influencers?.map((influencer: any, index: number) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <InfluencerCard 
                            influencer={influencer} 
                            index={index} 
                            onSave={(field, value) => saveInfluencersData('influencers', index, field, value)} 
                            onDelete={() => {
                              const updatedInfluencers = influencersData.influencers.filter((_: any, i: number) => i !== index);
                              setInfluencersData({ ...influencersData, influencers: updatedInfluencers });
                              saveDataToFirebase(planId, 'influencers', { ...influencersData, influencers: updatedInfluencers });
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button 
                        variant="outlined" 
                        onClick={() => {
                          const newInfluencer = {
                            name: 'New Influencer',
                            focus: 'Gaming',
                            followers: '100K',
                            tier: 'Mid',
                            status: 'open',
                            youtube: '',
                            twitch: '',
                            twitter: ''
                          };
                          const updatedInfluencers = [...(influencersData.influencers || []), newInfluencer];
                          setInfluencersData({ ...influencersData, influencers: updatedInfluencers });
                          saveDataToFirebase(planId, 'influencers', { ...influencersData, influencers: updatedInfluencers });
                        }}
                        startIcon={<Typography variant="h6">+</Typography>}
                      >
                        Add Influencer
                      </Button>
                    </Box>

                    <Typography variant="h5" sx={{ mt: 4, mb: 3 }}>
                      <EditableText 
                        value={influencersData?.tiktokSectionTitle || 'TikTok Creators'} 
                        onSave={(newValue) => saveInfluencersData('tiktokSectionTitle', newValue)} 
                        variant="h6"
                      />
                    </Typography>

                    <Grid container spacing={3}>
                      {influencersData?.tiktokCreators?.map((creator: any, index: number) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                          <TikTokCreatorCard 
                            creator={creator} 
                            index={index} 
                            onSave={(field, value) => saveInfluencersData('tiktokCreators', index, field, value)} 
                            onDelete={() => {
                              const updatedCreators = influencersData.tiktokCreators.filter((_: any, i: number) => i !== index);
                              setInfluencersData({ ...influencersData, tiktokCreators: updatedCreators });
                              saveDataToFirebase(planId, 'influencers', { ...influencersData, tiktokCreators: updatedCreators });
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button 
                        variant="outlined" 
                        onClick={() => {
                          const newCreator = {
                            name: 'New TikTok Creator',
                            focus: 'Gaming',
                            followers: '500K',
                            tier: 'Mid',
                            status: 'open',
                            tiktok: ''
                          };
                          const updatedCreators = [...(influencersData.tiktokCreators || []), newCreator];
                          setInfluencersData({ ...influencersData, tiktokCreators: updatedCreators });
                          saveDataToFirebase(planId, 'influencers', { ...influencersData, tiktokCreators: updatedCreators });
                        }}
                        startIcon={<Typography variant="h6">+</Typography>}
                      >
                        Add TikTok Creator
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {view === 'roadmap' && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={roadmapData?.title || 'Development Roadmap'} 
                        onSave={(newValue) => {
                          const updatedData = { ...roadmapData, title: newValue };
                          setRoadmapData(updatedData);
                          saveDataToFirebase(planId, 'roadmap', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={roadmapData?.description || 'This roadmap outlines the key development milestones that will support our marketing efforts. Each quarter focuses on specific objectives that align with our marketing strategy and player expectations.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...roadmapData, description: newValue };
                          setRoadmapData(updatedData);
                          saveDataToFirebase(planId, 'roadmap', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Box sx={{ mt: 4, position: 'relative' }}>
                      {roadmapData?.milestones?.map((milestone: any, index: number) => (
                        <Box 
                          key={index} 
                          sx={{ 
                            display: 'flex', 
                            position: 'relative', 
                            pb: 3,
                            pl: 1
                          }}
                          onMouseEnter={() => setHoveredMilestone(index)}
                          onMouseLeave={() => setHoveredMilestone(null)}
                        >
                          {/* Timeline connector */}
                          {index < (roadmapData.milestones.length - 1) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 24, // center of the icon
                                top: 32, // below icon
                                height: 'calc(100% - 24px)',
                                borderLeft: '2px solid',
                                borderColor: 'divider',
                              }}
                            />
                          )}

                          {/* Icon */}
                          <Box sx={{ position: 'relative', zIndex: 1, mr: 2, flexShrink: 0 }}>
                            <Box sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '8px',
                              bgcolor: '#EBE5FF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6938EF',
                              mt: '4px'
                            }}>
                              <DateRangeIcon sx={{ fontSize: 18 }} />
                            </Box>
                          </Box>

                          {/* Content */}
                          <Box sx={{ flex: 1, pt: 0.5 }}>
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                              <EditableText 
                                value={milestone.title} 
                                onSave={(newValue) => saveRoadmapData('milestones', index, 'title', newValue)} 
                                variant="h6"
                              />
                            </Typography>
                            
                            <ul style={{ paddingLeft: 20, marginTop: 8, marginBottom: 8, listStyle: 'disc' }}>
                              {milestone.features.map((feature: any, featureIndex: number) => (
                                <li key={featureIndex}>
                                  <EditableArrayItem
                                    value={feature.description}
                                    onSave={(newValue) => {
                                      const updatedData = { ...roadmapData };
                                      updatedData.milestones[index].features[featureIndex].description = newValue;
                                      setRoadmapData(updatedData);
                                      saveDataToFirebase(planId, 'roadmap', updatedData);
                                    }}
                                    onDelete={() => {
                                      const updatedData = { ...roadmapData };
                                      updatedData.milestones[index].features.splice(featureIndex, 1);
                                      setRoadmapData(updatedData);
                                      saveDataToFirebase(planId, 'roadmap', updatedData);
                                    }}
                                  />
                                </li>
                              ))}
                            </ul>
                            <AddItemButton 
                              onAdd={() => {
                                const updatedData = { ...roadmapData };
                                updatedData.milestones[index].features.push({ description: 'New Feature' });
                                setRoadmapData(updatedData);
                                saveDataToFirebase(planId, 'roadmap', updatedData);
                              }}
                              label="Feature"
                            />
                          </Box>
                          
                          {/* Delete Milestone Button */}
                          {hoveredMilestone === index && (
                              <IconButton 
                                  size="small" 
                                  onClick={() => {
                                      const updatedMilestones = roadmapData.milestones.filter((_: any, i: number) => i !== index);
                                      setRoadmapData({ ...roadmapData, milestones: updatedMilestones });
                                      saveDataToFirebase(planId, 'roadmap', { ...roadmapData, milestones: updatedMilestones });
                                  }}
                                  sx={{ 
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      color: 'error.main', 
                                  }}
                              >
                                  <DeleteIcon />
                              </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button 
                        variant="outlined"
                        onClick={() => {
                          const newMilestone = {
                            title: 'New Milestone',
                            features: [{ description: 'New feature description' }]
                          };
                          const updatedMilestones = [...(roadmapData?.milestones || []), newMilestone];
                          setRoadmapData({ ...roadmapData, milestones: updatedMilestones });
                          saveDataToFirebase(planId, 'roadmap', { ...roadmapData, milestones: updatedMilestones });
                        }}
                      >
                        Add Milestone
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {view === 'execution' && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h4" component="h2" gutterBottom>
                      <EditableText 
                        value={executionData?.title || 'Checklist'} 
                        onSave={(newValue) => {
                          const updatedData = { ...executionData, title: newValue };
                          setExecutionData(updatedData);
                          saveDataToFirebase(planId, 'execution', updatedData);
                        }} 
                        variant="h4"
                      />
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      <EditableText 
                        value={executionData?.description || 'This framework outlines the processes that will ensure the efficient and accountable execution of this marketing plan.'} 
                        onSave={(newValue) => {
                          const updatedData = { ...executionData, description: newValue };
                          setExecutionData(updatedData);
                          saveDataToFirebase(planId, 'execution', updatedData);
                        }} 
                        variant="body1"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Typography>
                    
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" gutterBottom>
                        Update Type Management
                      </Typography>
                      
                      {marketingUpdates.length === 0 ? (
                        <Card sx={{ border: '2px dashed', borderColor: 'divider', p: 3, textAlign: 'center' }}>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Marketing Update Types Available
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Update types need to be configured in the Settings collection under "marketingupdates" document.
                          </Typography>
                        </Card>
                      ) : (
                        <Grid container spacing={3}>
                          {marketingUpdates.map((updateType, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Card sx={{ height: '100%' }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                      {updateType}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        // Open edit modal for this update type
                                        setSelectedUpdateType(updateType);
                                        setEditModalOpen(true);
                                      }}
                                      sx={{ 
                                        color: 'primary.main',
                                        '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  
                                  <Box sx={{ mt: 2 }}>
                                    {!executionData?.updateTaskAssignments?.[updateType] || executionData.updateTaskAssignments[updateType].length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No tasks assigned
                                      </Typography>
                                    ) : (
                                      <Box>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                          Assigned Tasks ({executionData.updateTaskAssignments[updateType].length}):
                                        </Typography>
                                        {executionData.updateTaskAssignments[updateType].map((taskType: string, taskIndex: number) => (
                                          <Box 
                                            key={taskIndex} 
                                            sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              justifyContent: 'space-between',
                                              py: 0.5,
                                              px: 1,
                                              mb: 0.5,
                                              backgroundColor: 'action.hover',
                                              borderRadius: 1
                                            }}
                                          >
                                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                              {taskType}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              onClick={() => saveUpdateTaskAssignment(updateType, taskType, false)}
                                              sx={{ 
                                                color: 'error.main',
                                                '&:hover': { backgroundColor: 'error.light', color: 'white' },
                                                width: 20,
                                                height: 20
                                              }}
                                            >
                                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>-</Typography>
                                            </IconButton>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </main>
      </div>

      {/* Edit Task Assignment Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Tasks to "{selectedUpdateType}"
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Available Tasks
            </Typography>
            {marketingTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No marketing tasks available. Tasks need to be configured in the Settings collection under "marketingtasks" document.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {marketingTasks.map((taskType, index) => {
                  const isAssigned = executionData?.updateTaskAssignments?.[selectedUpdateType]?.includes(taskType) || false;
                  return (
                    <Grid item xs={12} sm={6} key={index}>
                      <Card 
                        sx={{ 
                          border: isAssigned ? '2px solid' : '1px solid',
                          borderColor: isAssigned ? 'primary.main' : 'divider',
                          backgroundColor: isAssigned ? 'primary.light' : 'background.paper',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: isAssigned ? 'primary.main' : 'action.hover',
                            color: isAssigned ? 'white' : 'inherit'
                          }
                        }}
                        onClick={() => saveUpdateTaskAssignment(selectedUpdateType, taskType, !isAssigned)}
                      >
                        <CardContent sx={{ py: 2, px: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontWeight: isAssigned ? 'bold' : 'normal' }}>
                              {taskType}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {isAssigned && (
                                <Typography variant="body2" sx={{ mr: 1, color: 'inherit' }}>
                                  Assigned
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: '2px solid',
                                  borderColor: isAssigned ? 'white' : 'primary.main',
                                  backgroundColor: isAssigned ? 'white' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {isAssigned && (
                                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                    ✓
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MarketingExecution; 